import { defineComponent as _defineComponent } from 'vue'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
  import HTMLRenderer from '@/components/common/HTMLRenderer.vue'
  import { CourseModule } from '@/store/modules/course'
  import useCourse from '@/composables/useCourse'
  import useDuration from '@/composables/useDuration'
  import ProgressBar from '@/components/common/ProgressBar.vue'
  import useCommonMixin from '@/composables/useCommonMixin'
  import Drag from '@/components/common/Drag.vue'
  import useIcons from '@/composables/useIcons'
  import SVGRenderer from '@/components/common/SVGRenderer.vue'
  import useEditor from '@/composables/useEditor'
  import { EditorModule } from '@/store/modules/editor'
  import useSegment from '@/composables/useSegment'
  import useBreakpoint from '@/composables/useBreakpoint'
  import { useRoute, useRouter } from 'vue-router/composables'
  import eventBus from '@/main'
  import useI18n from '@/composables/useI18n'
  import type { PropType } from 'vue'
  import type { CourseItem } from '@/services/interfaces/Course'

  
export default /*#__PURE__*/_defineComponent({
  __name: 'CourseTile',
  props: {
    course: {
      type: Object as PropType<CourseItem>,
      required: true,
    },
    sectionId: {
      type: String,
      default: null,
    },
    maxHeight: {
      type: Number,
      default: 0,
    },
    hideBackground: {
      type: Boolean,
      default: false,
    },
    isDraggable: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['course-data', 'clicked-drag'],
  setup(__props, { emit }) {

const props = __props;

  
  

  const router = useRouter()
  const route = useRoute()
  const { formatDurationToHHMM } = useDuration()
  const { guidedQuizDuration, canBuyCourses, courseGroupIds, productPrice } = useCourse()
  const { currencyFormatDE, isKnowledgeCheckCompany } = useCommonMixin()
  const { starIcon, cardEmptyImage, openCourseIcon, unlockIcon, lockIcon } = useIcons()
  const { inEditorMode: editMode, isEditor } = useEditor(route)
  const { isLargeDesktop, isMobileDevice, isDesktopDevice, current } = useBreakpoint()
  const { courseItemClicked, courseUpsellModalShown } = useSegment()
  const { translateString } = useI18n()

  const el = ref(null as HTMLDivElement | null)
  const hovering = ref(false)

  const inEditorMode = computed(() => isLargeDesktop.value && editMode.value)

  const showHideBackground = computed(
    () => !props.course.visibleToCourseSection && props.sectionId !== 'unassigned-section-id' && !props.hideBackground,
  )

  const computedHeight = computed(() => (props.maxHeight ? props.maxHeight + 'px' : 'auto'))

  const courseDuration = computed(() => {
    const moduleDuration =
      CourseModule.coursesModuleMap[props.course.id]?.reduce((acc, mod) => {
        acc += guidedQuizDuration.value(mod.guidedQuiz?.numberOfQuestions || 0)
        return acc
      }, 0) || 0
    return formatDurationToHHMM((props.course.duration || 0) + moduleDuration)
  })

  const showCoursePrice = computed(
    () => productPrice.value(props.course) && canBuyCourses.value && !props.course?.isIddCourse,
  )

  const visibleGroupsLength = computed(() => courseGroupIds.value(props.course.id).length)

  const visibleForXGroups = computed(() =>
    translateString('trainings.visibleForXGroups', { groupsCount: visibleGroupsLength.value }),
  )

  const showIsProgressTooltip = computed(() => {
    return isEditor.value && !inEditorMode.value && props.course.state === 'in_progress'
  })

  const canAccessKnowledgeCheckCourse = computed(() =>
    isKnowledgeCheckCompany.value ? isKnowledgeCheckCompany.value && props.course.knowledgeCheck : true,
  )

  const colSpan = computed(() => {
    if (['xxl', 'xxxl'].includes(current.value)) return 4
    if (['lg', 'xl'].includes(current.value)) return 3
    if (['sm', 'md'].includes(current.value)) return 2
    return 1
  })

  const courseTags = props.course.tags.map((t) => t.title).join(', ')

  const openCourseAction = (allowInEditMode: boolean) => {
    if (!allowInEditMode && inEditorMode.value) return
    if (isKnowledgeCheckCompany.value) {
      if (props.course.knowledgeCheck) {
        routeToKnowledgeCheckInfoOrCourseDetails('KnowledgeCheckInfo')
      }
      return
    }
    courseItemClicked(props.course.id, props.course.title, props.course.state, 'trainings_page')
    if (props.course.state === 'ready' || isEditor.value) {
      routeToKnowledgeCheckInfoOrCourseDetails('CourseDetails')
      eventBus.$emit('turn-off-loader')
    } else {
      courseUpsellModalShown(props.course.id, props.course.title, 'trainings_page')
      showUpsellModal()
    }
  }

  const routeToKnowledgeCheckInfoOrCourseDetails = (routeName: 'CourseDetails' | 'KnowledgeCheckInfo') => {
    router
      .push({
        name: routeName,
        params: { courseId: props.course.id },
      })
      .catch(() => {
        return
      })
  }

  const showUpsellModal = () => {
    eventBus.$emit('show-modal', {
      modalContentComponent: 'UpsellModal',
      cssClass: 'upsell',
      modalProps: {
        recommendedCourse: props.course,
      },
      modalCloseCallback: (callback: () => void) => {
        if (typeof callback === 'function') callback()
      },
    })
  }

  const promoteCourse = () => {
    CourseModule.promoteSectionCourse({
      sectionId: props.sectionId,
      courseId: props.course.id,
      promoted: !props.course.promoted,
    }).then(() => {
      handleHidePromotedCallback('promoted')
    })
  }

  const hideCourse = () => {
    eventBus.$emit('turn-on-loader')
    CourseModule.hideSectionCourse({
      sectionId: props.sectionId,
      courseId: props.course.id,
      visible: !props.course.visibleToCourseSection,
    }).then(() => {
      handleHidePromotedCallback('visibleToCourseSection')
    })
  }

  const handleHidePromotedCallback = (prop: 'visibleToCourseSection' | 'promoted') => {
    const section = EditorModule.courseSections?.find((s) => s.id === props.sectionId)
    const course = section!.assignedCourses.find((c) => (c as CourseItem).id === props.course.id) as CourseItem
    course[prop] = !course[prop]
    eventBus.$emit('turn-off-loader')
  }

  const showAssignCourseGroupModal = () => {
    eventBus.$emit('show-modal', {
      modalContentComponent: 'AssignCourseGroupModal',
      modalProps: {
        course: props.course,
        groupIds: courseGroupIds.value(props.course.id),
        sectionId: props.sectionId,
      },
      cssClass: 'assign-course-group-modal',
      modalCloseCallback: (callback: () => void) => {
        if (typeof callback === 'function') callback()
      },
    })
  }

  const courseTitleRef = ref(null as HTMLHeadingElement | null)
  const courseIndex = () => {
    if (!el.value) return -1
    if (!el.value.parentNode) return -1
    return [...el.value.parentNode!.children].filter((el) => el.classList.contains('eligible')).indexOf(el.value!)
  }

  const sendCourseData = () => {
    courseTitleRef.value?.style.setProperty('height', 'auto')
    nextTick(() => {
      emit('course-data', {
        sectionId: props.sectionId,
        courseId: props.course.id,
        rowNum: Math.ceil((courseIndex() + 1) / colSpan.value),
        titleHeight: courseTitleRef.value?.offsetHeight || 0,
        state: props.course.state,
      })
      courseTitleRef.value?.style.setProperty('height', computedHeight.value)
    })
  }

  const timerId = ref(0)
  watch(current, () => {
    if (timerId.value) window.clearTimeout(timerId.value)
    timerId.value = window.setTimeout(() => {
      sendCourseData()
    }, 1000)
  })

  onMounted(() => {
    //wait for animation to end
    window.setTimeout(() => {
      sendCourseData()
    }, 1000)
  })

  onBeforeUnmount(() => {
    // resetting courses that will be removed cos they are in progress when device orientation changes
    emit('course-data', {
      sectionId: props.sectionId,
      courseId: props.course.id,
      rowNum: Math.ceil((courseIndex() + 1) / colSpan.value),
      titleHeight: 0,
      state: props.course.state,
    })
  })

return { __sfc: true,props, emit, router, route, formatDurationToHHMM, guidedQuizDuration, canBuyCourses, courseGroupIds, productPrice, currencyFormatDE, isKnowledgeCheckCompany, starIcon, cardEmptyImage, openCourseIcon, unlockIcon, lockIcon, editMode, isEditor, isLargeDesktop, isMobileDevice, isDesktopDevice, current, courseItemClicked, courseUpsellModalShown, translateString, el, hovering, inEditorMode, showHideBackground, computedHeight, courseDuration, showCoursePrice, visibleGroupsLength, visibleForXGroups, showIsProgressTooltip, canAccessKnowledgeCheckCourse, colSpan, courseTags, openCourseAction, routeToKnowledgeCheckInfoOrCourseDetails, showUpsellModal, promoteCourse, hideCourse, handleHidePromotedCallback, showAssignCourseGroupModal, courseTitleRef, courseIndex, sendCourseData, timerId, computed, nextTick, onBeforeUnmount, onMounted, ref, watch, HTMLRenderer, CourseModule, useCourse, useDuration, ProgressBar, useCommonMixin, Drag, useIcons, SVGRenderer, useEditor, EditorModule, useSegment, useBreakpoint, useRoute, useRouter, eventBus, useI18n }
}

})